import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  API_BASE,
  api,
} from "../api";


function clean(value) {
  return String(
    value ?? ""
  ).trim();
}


function safeReaderDestination(
  value
) {
  const destination =
    clean(value);

  return /^\/reader\/childrens-book\/[a-fA-F0-9]{24}$/
    .test(destination)
      ? destination
      : "";
}


function readerAssetUrl(
  workId,
  assetId
) {
  const cleanWorkId =
    clean(workId);

  const cleanAssetId =
    clean(assetId);

  if (
    !cleanWorkId ||
    !cleanAssetId
  ) {
    return "";
  }

  return `${API_BASE}/api/reader/childrens-book/${encodeURIComponent(
    cleanWorkId
  )}/assets/${encodeURIComponent(
    cleanAssetId
  )}`;
}


function languageLabel(
  value
) {
  const language =
    clean(value)
      .toLowerCase();

  if (
    language.startsWith(
      "fr"
    )
  ) {
    return "Français";
  }

  if (
    language.startsWith(
      "en"
    )
  ) {
    return "English";
  }

  return "";
}


function ChildrensBookDiscoveryCover({
  workId = "",
  cover = null,
  title = "",
}) {
  const [
    imageUrl,
    setImageUrl,
  ] = useState("");

  const assetId =
    cover?.available === true
      ? clean(
          cover?.assetId
        )
      : "";

  useEffect(
    () => {
      let cancelled =
        false;

      let objectUrl =
        "";

      const assetUrl =
        readerAssetUrl(
          workId,
          assetId
        );

      setImageUrl("");

      if (!assetUrl) {
        return () => {};
      }

      async function loadCover() {
        try {
          const response =
            await fetch(
              assetUrl,
              {
                method:
                  "GET",

                credentials:
                  "include",
              }
            );

          if (
            !response.ok ||
            cancelled
          ) {
            return;
          }

          const blob =
            await response.blob();

          if (
            cancelled ||
            !blob ||
            Number(
              blob.size
            ) <= 0
          ) {
            return;
          }

          if (
            typeof URL ===
              "undefined" ||
            typeof URL
              .createObjectURL !==
              "function"
          ) {
            return;
          }

          objectUrl =
            URL.createObjectURL(
              blob
            );

          if (!cancelled) {
            setImageUrl(
              objectUrl
            );
          }
        } catch (_error) {
          if (!cancelled) {
            setImageUrl("");
          }
        }
      }

      loadCover();

      return () => {
        cancelled =
          true;

        if (
          objectUrl &&
          typeof URL !==
            "undefined" &&
          typeof URL
            .revokeObjectURL ===
            "function"
        ) {
          URL.revokeObjectURL(
            objectUrl
          );
        }
      };
    },
    [
      workId,
      assetId,
    ]
  );

  const label =
    clean(title) ||
    "Published Children’s Book";

  if (!imageUrl) {
    return (
      <div
        aria-label={`${label} cover unavailable`}
        style={{
          width: "min(100%, 160px)",
          aspectRatio: "2 / 3",
          margin: "0 auto",
          display: "grid",
          placeItems: "center",
          borderRadius: 14,
          border:
            "1px solid #e2e8f0",
          background: "#f8fafc",
          color: "#64748b",
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        Cover
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={`${label} cover`}
      style={{
        display: "block",
        width: "min(100%, 160px)",
        aspectRatio: "2 / 3",
        margin: "0 auto",
        objectFit: "cover",
        borderRadius: 14,
        border:
          "1px solid #e2e8f0",
        background: "#f8fafc",
      }}
    />
  );
}


export default function ChildrensBookDiscovery() {
  const [
    items,
    setItems,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");

  const loadBooks =
    useCallback(
      async () => {
        setLoading(true);
        setError("");

        try {
          const response =
            await api.get(
              "/api/reader/childrens-book/discover?limit=24"
            );

          setItems(
            Array.isArray(
              response?.items
            )
              ? response.items
              : []
          );
        } catch {
          setItems([]);

          setError(
            "Published books could not be loaded right now."
          );
        } finally {
          setLoading(false);
        }
      },
      []
    );

  useEffect(
    () => {
      loadBooks();
    },
    [loadBooks]
  );

  function openBook(
    work
  ) {
    const destination =
      safeReaderDestination(
        work
          ?.reader
          ?.destination
      );

    if (!destination) {
      return;
    }

    window.location.href =
      destination;
  }

  return (
    <main
      className="ns-page"
      data-testid="childrens-book-discovery"
      style={{
        maxWidth: 1100,
        margin: "0 auto",
      }}
    >
      <header
        className="ns-card"
        style={{
          marginBottom: 20,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems:
              "flex-start",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 800,
                letterSpacing:
                  "0.06em",
                textTransform:
                  "uppercase",
                color: "#64748b",
              }}
            >
              NextScenes Reader
            </div>

            <h1
              className="ns-page-title"
            >
              Children’s Books
            </h1>

            <p
              className="ns-page-subtitle"
            >
              Discover published books
              available to preview on
              NextScenes.
            </p>
          </div>

          <button
            type="button"
            className="ns-button ns-button-outline"
            onClick={
              loadBooks
            }
            disabled={
              loading
            }
          >
            {loading
              ? "Loading…"
              : "Refresh"}
          </button>
        </div>
      </header>

      {error ? (
        <div
          className="ns-card"
          style={{
            marginBottom: 18,
          }}
        >
          {error}
        </div>
      ) : null}

      {loading &&
      items.length === 0 ? (
        <div className="ns-card">
          Loading published books…
        </div>
      ) : null}

      {!loading &&
      !error &&
      items.length === 0 ? (
        <div className="ns-card">
          <h2
            style={{
              marginTop: 0,
            }}
          >
            No published books yet
          </h2>

          <p
            className="ns-page-subtitle"
          >
            Published Children’s Books
            that you can preview will
            appear here.
          </p>
        </div>
      ) : null}

      {items.length ? (
        <section
          aria-label="Published Children’s Books"
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(250px, 1fr))",
            gap: 18,
          }}
        >
          {items.map(
            (work) => {
              const workId =
                clean(
                  work?.workId
                );

              const title =
                clean(
                  work?.title
                ) ||
                "Untitled Children’s Book";

              const author =
                clean(
                  work
                    ?.creator
                    ?.name
                );

              const language =
                languageLabel(
                  work?.language
                );

              const destination =
                safeReaderDestination(
                  work
                    ?.reader
                    ?.destination
                );

              const cover =
                work?.reader?.cover &&
                typeof work
                  .reader
                  .cover ===
                  "object"
                  ? work
                      .reader
                      .cover
                  : null;

              return (
                <article
                  key={
                    workId ||
                    destination ||
                    title
                  }
                  className="ns-card"
                  style={{
                    display: "grid",
                    alignContent:
                      "space-between",
                    gap: 18,
                    minHeight: 210,
                  }}
                >
                  <ChildrensBookDiscoveryCover
                    workId={
                      workId
                    }
                    cover={
                      cover
                    }
                    title={
                      title
                    }
                  />

                  <div>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 800,
                        textTransform:
                          "uppercase",
                        letterSpacing:
                          "0.05em",
                        color:
                          "#64748b",
                        marginBottom: 8,
                      }}
                    >
                      Published Book
                    </div>

                    <h2
                      style={{
                        margin:
                          "0 0 8px",
                        fontSize: 22,
                        lineHeight: 1.25,
                      }}
                    >
                      {title}
                    </h2>

                    {author ? (
                      <div
                        style={{
                          fontWeight: 700,
                          marginBottom: 6,
                        }}
                      >
                        by {author}
                      </div>
                    ) : null}

                    {language ? (
                      <div
                        className="ns-card-meta"
                      >
                        {language}
                      </div>
                    ) : null}
                  </div>

                  <button
                    type="button"
                    className="ns-button ns-button-primary"
                    disabled={
                      !destination
                    }
                    onClick={() =>
                      openBook(
                        work
                      )
                    }
                  >
                    Preview Book
                  </button>
                </article>
              );
            }
          )}
        </section>
      ) : null}
    </main>
  );
}
